/**
 * Verification Page
 * Stripe Connect onboarding for landlords.
 * Routes: /verification, /verification/refresh
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Clock, AlertTriangle, Circle } from 'lucide-react';
import {
  getStripeConnectStatus,
  startStripeConnectOnboarding,
  refreshStripeConnectLink,
  ApiClientError,
  type StripeConnectStatus,
} from '../lib/api';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { formatDateTime } from '../utils/date';
import { StripeConnectInfoModal } from '../components/StripeConnectInfoModal';

export function VerificationPage() {
  const [status, setStatus] = useState<StripeConnectStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasAccount, setHasAccount] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  async function loadStatus() {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getStripeConnectStatus();
      setStatus(response);
      setHasAccount(true);
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 404) {
        setHasAccount(false);
      } else if (err instanceof ApiClientError && err.message === 'Connect account not found') {
        setHasAccount(false);
      } else {
        setError('Failed to load verification status');
        console.error(err);
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleStartOnboarding() {
    try {
      setIsActionLoading(true);
      setError(null);
      const result = await startStripeConnectOnboarding();
      window.location.href = result.onboardingUrl;
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.message === 'Connect account already exists') {
          await handleRefreshLink();
          return;
        }
        setError(err.message);
      } else {
        setError('Failed to start verification');
        console.error(err);
      }
      setIsActionLoading(false);
    }
  }

  async function handleRefreshLink() {
    try {
      setIsActionLoading(true);
      setError(null);
      const result = await refreshStripeConnectLink();
      window.location.href = result.onboardingUrl;
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('Failed to get onboarding link');
        console.error(err);
      }
      setIsActionLoading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto px-[var(--page-padding-x)] pb-6 pt-4" data-testid="verification-page">
        <Skeleton className="h-4 w-32 mb-6" />
        <Skeleton className="h-7 w-56 mb-2" />
        <Skeleton className="h-4 w-72 mb-8" />
        <Skeleton className="h-32 w-full rounded-lg" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-[var(--page-padding-x)] pb-6 pt-4" data-testid="verification-page">
      <Link
        to="/settings/ach-accounts"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
        data-testid="verification-back-link"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        ACH Accounts
      </Link>

      <h1 className="text-xl font-semibold text-foreground m-0">Payment Verification</h1>
      <p className="text-sm text-muted-foreground mt-1 mb-6">
        Verify your identity to receive rent payments via ACH bank transfer.
      </p>

      {error && (
        <div className="mb-6 p-3 rounded-lg border border-destructive/30 bg-destructive/5">
          <p className="text-sm text-destructive m-0">{error}</p>
        </div>
      )}

      {!hasAccount ? (
        <NotStartedCard onStart={handleStartOnboarding} isLoading={isActionLoading} />
      ) : status?.status === 'active' ? (
        <VerifiedCard verifiedAt={status.verifiedAt} />
      ) : status?.status === 'restricted' ? (
        <RestrictedCard onResume={handleRefreshLink} isLoading={isActionLoading} />
      ) : (
        <OnboardingCard onResume={handleRefreshLink} isLoading={isActionLoading} />
      )}
    </div>
  );
}

function NotStartedCard({ onStart, isLoading }: { onStart: () => void; isLoading: boolean }) {
  const [showInfoModal, setShowInfoModal] = useState(false);
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <div className="flex items-start gap-3 mb-4">
        <Circle className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
        <div>
          <h2 className="text-base font-medium text-foreground m-0">Not started</h2>
          <p className="text-sm text-muted-foreground mt-1 m-0">
            Complete a quick identity verification to start receiving rent payments directly to your
            bank account. You&apos;ll be redirected to our payment partner to securely verify your
            identity — takes about 5 minutes. You&apos;ll need your SSN or EIN and bank account
            details.
          </p>
        </div>
      </div>
      <Button
        onClick={() => setShowInfoModal(true)}
        disabled={isLoading}
        className="min-h-[44px]"
        data-testid="start-verification-button"
      >
        {isLoading ? 'Setting up...' : 'Start Verification'}
      </Button>
      <StripeConnectInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        onContinue={() => {
          setShowInfoModal(false);
          onStart();
        }}
        isLoading={isLoading}
      />
    </div>
  );
}

function VerifiedCard({ verifiedAt }: { verifiedAt: string | null }) {
  return (
    <div className="rounded-lg border border-success/30 bg-success/5 p-5">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="h-5 w-5 text-success shrink-0 mt-0.5" aria-hidden="true" />
        <div>
          <h2 className="text-base font-medium text-foreground m-0">Verified</h2>
          <p className="text-sm text-muted-foreground mt-1 m-0">
            Your identity has been verified. You can receive rent payments via ACH bank transfer.
          </p>
          {verifiedAt && (
            <p className="text-xs text-muted-foreground mt-2 m-0">
              Verified{' '}
              {formatDateTime(verifiedAt, { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function OnboardingCard({ onResume, isLoading }: { onResume: () => void; isLoading: boolean }) {
  return (
    <div className="rounded-lg border border-warning/30 bg-warning/5 p-5">
      <div className="flex items-start gap-3 mb-4">
        <Clock className="h-5 w-5 text-warning shrink-0 mt-0.5" aria-hidden="true" />
        <div>
          <h2 className="text-base font-medium text-foreground m-0">Verification in progress</h2>
          <p className="text-sm text-muted-foreground mt-1 m-0">
            Your verification is not yet complete. Continue where you left off.
          </p>
        </div>
      </div>
      <Button onClick={onResume} disabled={isLoading} className="min-h-[44px]" data-testid="continue-verification-button">
        {isLoading ? 'Loading...' : 'Continue Verification'}
      </Button>
    </div>
  );
}

function RestrictedCard({ onResume, isLoading }: { onResume: () => void; isLoading: boolean }) {
  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-5">
      <div className="flex items-start gap-3 mb-4">
        <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" aria-hidden="true" />
        <div>
          <h2 className="text-base font-medium text-destructive m-0">Action required</h2>
          <p className="text-sm text-muted-foreground mt-1 m-0">
            Additional information is needed to complete your verification.
          </p>
        </div>
      </div>
      <Button onClick={onResume} disabled={isLoading} className="min-h-[44px]" data-testid="complete-verification-button">
        {isLoading ? 'Loading...' : 'Complete Verification'}
      </Button>
    </div>
  );
}

